import { Box, Grid } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Currency, LoanStatusExternal, MerchantStructure, MetricAgentView } from '@nemuru-eng/js-api-client';
import { useMemo, useState } from 'react';

import { AgentScoreCard } from '@/components/modules/Metrics/ChannelAgentMetricViews/components/AgentScoreCard';
import { ChannelScoreCard } from '@/components/modules/Metrics/ChannelAgentMetricViews/components/ChannelScoreCard';
import { AgentMetricAnalyzer } from '@/components/modules/Metrics/ChannelAgentMetricViews/entities/MerchantAgentMetricAnalyzer';
import { useMetricsPagination } from '@/components/modules/Metrics/ChannelAgentMetricViews/hooks/useMetricsPagination';
import { MetricsListWrapper } from '@/components/modules/Metrics/shared/components/MetricsListWrapper';
import { TimeInterval } from '@/components/modules/Metrics/shared/entities/TimeInterval';
import { ScoreCardMetricKey } from '@/components/modules/Metrics/shared/types/ScoreCardMetricKey';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { PendingDataType } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

interface ChannelAgentMetricsProps {
  merchantStructure: MerchantStructure;
  timeInterval: TimeInterval;
  setTimeInterval: React.Dispatch<React.SetStateAction<TimeInterval>>;
  hasRequested: boolean;
  currency: Currency;
  selectedLoanStatusExternal: LoanStatusExternal;
  selectedMetricsType: ScoreCardMetricKey;
  filteredMetrics?: MetricAgentView[];
  hideChannelMetrics?: boolean;
}

export const ChannelAndAgentMetrics = ({
  merchantStructure,
  timeInterval,
  filteredMetrics,
  hasRequested,
  currency,
  selectedLoanStatusExternal,
  selectedMetricsType,
  hideChannelMetrics,
}: ChannelAgentMetricsProps) => {
  const { customBorderRadius } = useCustomTheme();
  const { isMobile } = useScreen();

  const [selectedChannelId, setSelectedChannelId] = useState<undefined | string>(undefined);

  const metricsAnalyzer = useMemo(() => new AgentMetricAnalyzer(timeInterval, filteredMetrics), [filteredMetrics]);

  const agentMetrics = selectedChannelId
    ? metricsAnalyzer
        .agentMetrics(selectedLoanStatusExternal, merchantStructure, selectedMetricsType)
        .filter((agentScore) => agentScore.channelId === selectedChannelId)
    : metricsAnalyzer.agentMetrics(selectedLoanStatusExternal, merchantStructure, selectedMetricsType);

  const channelMetrics = metricsAnalyzer.channelMetrics(
    selectedLoanStatusExternal,
    merchantStructure,
    selectedMetricsType,
  );

  const filteredChannelMetrics = channelMetrics.filter(
    (channelMetric) =>
      (channelMetric.totalLoansNumber.current > 0 && channelMetric.totalLoansNumber.current > 0) ||
      (channelMetric.totalLoansNumber.previous > 0 && channelMetric.totalLoansNumber.previous > 0),
  );
  const filteredAgentMetrics = agentMetrics.filter(
    (agentMetric) =>
      (agentMetric.totalLoansNumber.current > 0 && agentMetric.totalLoansNumber.current > 0) ||
      (agentMetric.totalLoansNumber.previous > 0 && agentMetric.totalLoansNumber.previous > 0),
  );

  const channelHasMetrics = filteredChannelMetrics.length > 0;
  const agentHasMetrics = filteredAgentMetrics.length > 0;

  const numberOfChannels = filteredChannelMetrics.length;
  const numberOfAgents = filteredAgentMetrics.length;

  const maxAgentValues = filteredAgentMetrics[0];
  const maxChannelValues = filteredChannelMetrics[0];

  const { onNext, onPrev, disableNext, disablePrev, page, rowsPerPage } = useMetricsPagination({
    filteredMetrics: filteredChannelMetrics,
  });

  const {
    onNext: onNextAgentPage,
    onPrev: onPrevAgentPage,
    setPage: setAgentPage,
    disableNext: disableNextAgentPage,
    disablePrev: disablePrevAgentPage,
    page: agentPage,
    rowsPerPage: agentRowsPerPage,
  } = useMetricsPagination({
    filteredMetrics: filteredAgentMetrics,
  });

  if (!filteredMetrics) return null;

  return (
    <Box display={'flex'} flexDirection={'column'} flexGrow={1} width={'100%'}>
      <Grid container spacing={isMobile ? 0 : 6}>
        {!hideChannelMetrics && (
          <Grid item xs={12} md={6}>
            <SubSectionWrapper title="CHANNELS">
              {hasRequested && (channelMetrics.length === 0 || channelHasMetrics === false) ? (
                <PendingDataWarning type={PendingDataType.NO_METRICS_INFORMATION} boxProps={{ height: 120 }} />
              ) : (
                <MetricsListWrapper
                  page={page}
                  rowsPerPage={rowsPerPage}
                  totalElementsValue={numberOfChannels}
                  onNext={onNext}
                  onPrev={onPrev}
                  disableNext={disableNext}
                  disablePrev={disablePrev}
                >
                  <>
                    {hasRequested === false &&
                      filteredChannelMetrics.map((i) => (
                        <Skeleton
                          key={`top-skeleton-${i}`}
                          variant="rect"
                          width={'100%'}
                          height={66}
                          style={{
                            marginBottom: 8,
                            borderRadius: customBorderRadius.paper,
                          }}
                        />
                      ))}
                    {hasRequested &&
                      channelHasMetrics &&
                      filteredChannelMetrics
                        .filter((_, i) => i >= page * rowsPerPage - rowsPerPage && i < page * rowsPerPage)
                        .map((metric, i) => (
                          <ChannelScoreCard
                            key={i}
                            currency={currency}
                            metric={metric}
                            selectedChannelId={selectedChannelId}
                            setSelectedChannelId={setSelectedChannelId}
                            selectedMetricsType={selectedMetricsType}
                            setPage={setAgentPage}
                            maxChannelValues={maxChannelValues}
                          />
                        ))}
                  </>
                </MetricsListWrapper>
              )}
            </SubSectionWrapper>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <SubSectionWrapper title="AGENTS">
            {hasRequested && (agentMetrics.length === 0 || agentHasMetrics === false) ? (
              <PendingDataWarning type={PendingDataType.NO_METRICS_INFORMATION} boxProps={{ height: 120 }} />
            ) : (
              <MetricsListWrapper
                page={agentPage}
                rowsPerPage={agentRowsPerPage}
                totalElementsValue={numberOfAgents}
                onNext={onNextAgentPage}
                onPrev={onPrevAgentPage}
                disableNext={disableNextAgentPage}
                disablePrev={disablePrevAgentPage}
              >
                <>
                  {hasRequested === false &&
                    filteredAgentMetrics.map((i) => (
                      <Skeleton
                        key={`channel-skeleton-${i}`}
                        variant="rect"
                        width={'100%'}
                        height={66}
                        style={{
                          marginBottom: 8,
                          borderRadius: customBorderRadius.paper,
                        }}
                      />
                    ))}
                  {hasRequested &&
                    agentHasMetrics &&
                    filteredAgentMetrics
                      .filter(
                        (_, i) =>
                          i >= agentPage * agentRowsPerPage - agentRowsPerPage && i < agentPage * agentRowsPerPage,
                      )
                      .map((metric, i) => (
                        <AgentScoreCard
                          key={i}
                          currency={currency}
                          metric={metric}
                          selectedMetricsType={selectedMetricsType}
                          maxAgentValues={maxAgentValues}
                        />
                      ))}
                </>
              </MetricsListWrapper>
            )}
          </SubSectionWrapper>
        </Grid>
      </Grid>
    </Box>
  );
};
